import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function MuizenbergPage() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    try {
      document.title =
        'Muizenberg Painters Cape Town | Beachfront Painting Services | Surfing Village Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Professional painting services in Muizenberg, Cape Town. Specialist beachfront and coastal painting for False Bay properties. Expert marine-grade coatings and colorful beach house finishes.'
        );
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Muizenberg painters, beach house painting Cape Town, False Bay coastal painting'
        );
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/muizenberg`
        );
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Muizenberg Painters Cape Town',
        description: 'Professional painting services in Muizenberg, Cape Town',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/muizenberg`,
        areaServed: {
          '@type': 'Place',
          name: 'Muizenberg, Cape Town, Western Cape, South Africa',
        },
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: -34.1033,
            longitude: 18.4700,
          },
          geoRadius: '10000',
        },
      });
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error setting up meta tags or JSON‑LD script:', err);
    }

    return () => {
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingScript && existingScript.textContent?.includes('Muizenberg Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Beach House Painting',
      description:
        'Specialized painting services for Muizenberg\'s iconic colorful beach houses and coastal properties with vibrant, weather-resistant finishes. Our approach celebrates the area\'s famous beach culture while providing superior protection against False Bay\'s coastal conditions. We use premium marine-grade materials that maintain brilliant colors despite intense sun exposure and salt air.',
      features: [
        'Vibrant color options',
        'Marine-grade protection',
        'UV-resistant finishes',
        'Beach house expertise',
        'Weather resistance',
        'Long-lasting brilliance'
      ],
    },
    {
      icon: 'ri-building-2-line',
      title: 'Beachfront Property Painting',
      description:
        'Comprehensive painting services for properties near Muizenberg Beach with advanced coastal-grade coatings designed to withstand False Bay\'s marine environment. Our protective systems defend against salt spray, wind, and moisture while maintaining the relaxed, colorful aesthetic that makes Muizenberg special and attracts visitors from around the world.',
      features: [
        'Coastal-grade materials',
        'Salt air protection',
        'Wind resistance',
        'Moisture barriers',
        'Beachfront expertise',
        'Extended durability'
      ],
    },
    {
      icon: 'ri-store-2-line',
      title: 'Commercial & Surf Shop Painting',
      description:
        'Professional painting for Muizenberg\'s surf shops, cafes, restaurants, and businesses serving the beach community. We understand the importance of maintaining the area\'s vibrant, welcoming atmosphere and offer flexible scheduling to minimize disruption. Our efficient processes ensure quick turnaround while delivering colorful, eye-catching results that attract customers.',
      features: [
        'After-hours service',
        'Vibrant finishes',
        'Brand color matching',
        'Quick completion',
        'Tourism-ready',
        'Professional standards'
      ],
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Exterior Coastal Painting',
      description:
        'Comprehensive exterior painting services specifically designed for Muizenberg\'s False Bay beachfront environment. Our weather-resistant systems protect against salt spray, wind, and intense sun exposure while maintaining the bright, cheerful colors that define Muizenberg\'s character. We use premium products formulated for coastal conditions and color retention.',
      features: [
        'Weather-resistant coatings',
        'Coastal protection',
        'Color retention',
        'Sun protection',
        'Wind resistance',
        'Comprehensive warranty'
      ],
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Interior Beach Style Painting',
      description:
        'Expert interior painting services for Muizenberg properties featuring beach-inspired finishes, bright color schemes, and relaxed coastal aesthetics. We work with eco-friendly, low-odor materials perfect for beach houses and family homes, creating light, airy interiors that complement the beachfront lifestyle and ocean views.',
      features: [
        'Beach-inspired colors',
        'Bright finishes',
        'Low-odor materials',
        'Furniture protection',
        'Detailed preparation',
        'Clean workmanship'
      ],
    },
    {
      icon: 'ri-tools-line',
      title: 'Restoration & Maintenance',
      description:
        'Comprehensive restoration and maintenance services for Muizenberg properties experiencing coastal weather damage or fading. Our expert approach addresses both aesthetic and structural concerns, including color restoration, crack repair, and protective coatings to ensure your property maintains its vibrant appearance and protection against False Bay\'s marine environment.',
      features: [
        'Color restoration',
        'Weather damage repair',
        'Crack restoration',
        'Waterproofing',
        'Preventive maintenance',
        'Structural protection'
      ],
    },
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Beach Property Assessment',
      description:
        'Comprehensive evaluation of your Muizenberg property, including inspection of coastal exposure, color preferences, and existing paint condition. We assess salt air impact, UV damage, and moisture issues to develop a customized strategy that delivers vibrant colors with superior coastal protection tailored to False Bay conditions.',
    },
    {
      step: 2,
      title: 'Color Consultation',
      description:
        'Expert consultation on vibrant, beach-appropriate colors and marine-grade coatings for coastal properties. We recommend premium UV-resistant products that maintain brilliant colors despite intense sun exposure and salt air. Our selections balance aesthetic appeal with superior performance in Muizenberg\'s demanding beachfront environment.',
    },
    {
      step: 3,
      title: 'Surface Preparation',
      description:
        'Thorough preparation including cleaning, salt deposit removal, repairs, and premium primer application. Our detailed prep work ensures optimal adhesion and longevity of vibrant colors, addressing any underlying issues caused by coastal exposure. We take extra care to protect surrounding areas and landscaping.',
    },
    {
      step: 4,
      title: 'Expert Application',
      description:
        'Professional application by experienced coastal painting specialists using advanced techniques and premium equipment. We employ multiple coat systems with proper curing times between applications to ensure maximum color vibrancy, durability, and weather resistance. Work is scheduled around weather conditions for optimal results.',
    },
    {
      step: 5,
      title: 'Quality Inspection',
      description:
        'Rigorous quality control inspection of all painted surfaces, checking for uniform coverage, color accuracy, proper adhesion, and excellent finish. We ensure every detail meets our high standards for beachfront properties and your expectations, with comprehensive documentation of all work performed.',
    },
    {
      step: 6,
      title: 'Maintenance Guidance',
      description:
        'Detailed maintenance recommendations and care instructions specific to beachfront properties in False Bay coastal environments. We provide guidance on cleaning, color preservation, inspection schedules, and preventive maintenance to maximize the lifespan and vibrancy of your coating system in Muizenberg\'s conditions.',
    },
  ];

  const recentProjects = [
    {
      title: 'Iconic Beach Hut Restoration',
      location: 'Muizenberg Beachfront',
      description:
        'Complete restoration of a famous colorful beach hut including vibrant exterior painting with premium UV-resistant coatings, structural repairs, and comprehensive weather protection. Project preserved the iconic appearance while providing modern coastal protection. Colors remain brilliant after two years of direct beach exposure.',
      image:
        'https://public.readdy.ai/ai/img_res/84461a0c801cd1ee01a0da289709d49d.jpg',
    },
    {
      title: 'Beachfront Surf Shop Makeover',
      location: 'Muizenberg Main Road',
      description:
        'Comprehensive painting of a popular surf shop including retail space, storage areas, and exterior facades. Project completed using after-hours scheduling to minimize business disruption. Included eye-catching color scheme, specialized coatings for high-traffic areas, and weather-resistant exterior finishes.',
      image:
        'https://readdy.ai/api/search-image?query=Surf%20shop%20painting%20in%20Muizenberg%2C%20colorful%20beachfront%20retail%20store%2C%20professional%20commercial%20painting%20Cape%20Town%2C%20vibrant%20beach%20business%20with%20surfing%20culture&width=400&height=300&seq=muizenberg-project-2&orientation=landscape',
    },
    {
      title: 'Coastal Family Home Transformation',
      location: 'Muizenberg Residential',
      description:
        'Complete interior and exterior painting of a 3-bedroom family home with ocean views. Project included bright beach-inspired color scheme, marine-grade exterior coatings, eco-friendly interior paints, and comprehensive waterproofing. Completed during school holidays to accommodate family schedule.',
      image:
        'https://readdy.ai/api/search-image?query=Family%20beach%20house%20painting%20in%20Muizenberg%2C%20coastal%20residential%20property%20with%20False%20Bay%20views%2C%20professional%20painting%20services%2C%20colorful%20home%20near%20surfing%20beach%20Cape%20Town&width=400&height=300&seq=muizenberg-project-3&orientation=landscape',
    },
    {
      title: 'Beachfront Cafe Renovation',
      location: 'Muizenberg Beach Promenade',
      description:
        'Complete painting of a beachfront cafe including dining areas, deck, and exterior facades. Included vibrant color scheme reflecting beach culture, specialized coatings for coastal exposure and high-traffic areas, and weather-resistant finishes. Project enhanced the welcoming atmosphere for beach visitors.',
      image:
        'https://readdy.ai/api/search-image?query=Beachfront%20cafe%20painting%20in%20Muizenberg%2C%20colorful%20coastal%20dining%20establishment%20with%20ocean%20views%2C%20professional%20commercial%20painting%20Cape%20Town%2C%20vibrant%20beach%20restaurant%20with%20surfing%20culture&width=400&height=300&seq=muizenberg-project-4&orientation=landscape',
    },
  ];

  const testimonials = [
    {
      name: 'Sophie Anderson',
      location: 'Muizenberg Beach House',
      rating: 5,
      text:
        'Absolutely love the vibrant colors on our beach house! The team understood exactly what we wanted and the marine-grade coatings have kept the colors brilliant for over two years despite constant sun and salt exposure. Professional service and the results are stunning. Highly recommend for beach properties.',
    },
    {
      name: 'Mark Petersen',
      location: 'Muizenberg Surf Shop',
      rating: 5,
      text:
        'Outstanding service for our surf shop. They worked after hours so we didn\'t lose business, and the eye-catching colors have attracted more customers. The quality is exceptional and the coatings have held up perfectly despite the coastal conditions. Great value and professional team.',
    },
    {
      name: 'Rachel Jacobs',
      location: 'Muizenberg Family Home',
      rating: 5,
      text:
        'The transformation of our home exceeded expectations. The beach-inspired colors are perfect and the eco-friendly materials were important to us. They worked around our family schedule and the house looks amazing. The coastal protection gives us peace of mind for years to come.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Colorful%20Muizenberg%20beach%20houses%20and%20surfing%20village%20Cape%20Town%20being%20painted%2C%20iconic%20vibrant%20beachfront%20properties%20with%20False%20Bay%20views%2C%20professional%20painters%20working%20on%20famous%20beach%20huts%2C%20stunning%20coastal%20community%20with%20mountain%20backdrop&width=1920&height=600&seq=muizenberg-hero-main&orientation=landscape"
            alt="Professional painting services in Muizenberg, Cape Town"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Vibrant <span className="text-yellow-400">Muizenberg Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Specialized beachfront painting services for Muizenberg&apos;s colorful coastal properties.
              Expert marine‑grade coatings and vibrant finishes for beach houses and businesses in this iconic False Bay surfing village.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Expert Painting Services for Muizenberg&apos;s Iconic Beach Community
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Muizenberg is one of Cape Town&apos;s most beloved and iconic beach suburbs, world-famous for its colorful beach huts, excellent surfing conditions, and vibrant beach culture. This historic seaside village demands painting services that understand both the unique challenges of False Bay coastal living and the importance of maintaining the bright, cheerful aesthetic that makes Muizenberg a destination for locals and tourists alike.
              </p>
              <p>
                Our specialized painting services for Muizenberg properties combine advanced marine-grade materials, expert color consultation, and deep understanding of False Bay coastal environmental factors. The area&apos;s beachfront location means properties are exposed to salt-laden air, intense UV radiation from sun reflection off sand and water, and moisture-rich conditions that can rapidly fade colors and deteriorate standard paint systems if not properly addressed with specialized coatings.
              </p>
              <p>
                Whether you own one of the famous colorful beach houses, manage a surf shop or cafe serving the beach community, or have a family home in the residential areas, our team delivers painting solutions that protect your investment while celebrating the vibrant character that defines Muizenberg. We understand that properties in this area represent both financial investments and important parts of the community&apos;s cultural identity, and our services are designed to preserve both aspects.
              </p>
              <p>
                The unique character of Muizenberg, with its relaxed beach lifestyle, active surfing culture, and strong sense of community, creates specific painting requirements. From maintaining the brilliant colors of beachfront properties to protecting commercial establishments serving beach visitors, our team has the expertise and experience to handle any coastal painting project while respecting the area&apos;s distinctive character and aesthetic values.
              </p>
              <p>
                Muizenberg&apos;s position on False Bay creates demanding conditions for painted surfaces, particularly for the iconic colorful beach huts and beachfront properties that define the area&apos;s visual identity. The combination of direct sun exposure, salt spray from ocean winds, sand abrasion, and high humidity can cause rapid color fading and paint deterioration if inferior products are used. Our comprehensive approach uses premium UV-resistant, marine-grade coatings specifically formulated to maintain brilliant colors and superior protection in these challenging conditions. We take pride in helping preserve Muizenberg&apos;s famous colorful appearance while ensuring properties receive the long-lasting protection they need to withstand the demanding coastal environment year after year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Beach Painting Services in Muizenberg
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Muizenberg&apos;s beachfront properties, colorful beach houses, and coastal businesses. Our services celebrate the area&apos;s vibrant character while providing superior coastal protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coastal Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Understanding Muizenberg&apos;s Unique Beach Painting Challenges
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Muizenberg&apos;s beachfront location on False Bay creates specific challenges for maintaining vibrant painted surfaces. The area&apos;s famous colorful beach huts and properties face constant exposure to intense UV radiation reflected from both the ocean and the white sand beach, creating a double reflection effect that causes rapid fading of inferior paint products. This makes the selection of premium UV-resistant coatings absolutely essential for maintaining the brilliant colors that define Muizenberg&apos;s character.
              </p>
              <p>
                Salt spray carried by ocean winds affects properties throughout Muizenberg, with beachfront locations experiencing particularly intense exposure. The combination of salt accumulation and moisture accelerates paint deterioration, causing premature fading, chalking, and peeling if standard painting products are used. Our marine-grade coating systems are specifically engineered to resist salt damage while maintaining color vibrancy, ensuring your property looks beautiful year after year.
              </p>
              <p>
                Wind-driven sand creates additional challenges for painted surfaces in Muizenberg. The fine sand particles can cause abrasion of paint films, particularly on surfaces facing the beach or exposed to prevailing winds. This abrasive action, combined with salt spray and UV exposure, creates a demanding environment that requires robust, high-quality coating systems designed specifically for beachfront conditions to ensure long-lasting protection and color retention.
              </p>
              <p>
                Moisture management is critical for Muizenberg properties due to the combination of ocean humidity, morning fog common in False Bay, and occasional wind-driven rain. This moisture-rich environment can penetrate building surfaces if proper waterproofing and coating systems are not employed. Our comprehensive approach addresses these moisture challenges through advanced preparation techniques, specialized primers, and multi-layer protection systems that keep properties dry while maintaining the vibrant colors that make Muizenberg special.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Specialized Painting Process for Muizenberg Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring vibrant colors and lasting protection for beachfront properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-black">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Muizenberg Properties Choose Our Beach Painting Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Color Vibrancy Expertise',
                    description:
                      'Specialized knowledge of UV-resistant coatings that maintain brilliant colors in Muizenberg\'s intense sun exposure. Our premium products are specifically formulated to resist fading and keep your property looking vibrant year after year despite challenging coastal conditions.',
                  },
                  {
                    title: 'Beach Culture Understanding',
                    description:
                      'Deep appreciation for Muizenberg\'s unique character and the importance of maintaining the colorful aesthetic that defines this iconic beach community. We celebrate the area\'s vibrant culture while delivering superior coastal protection.',
                  },
                  {
                    title: 'Marine-Grade Protection',
                    description:
                      'Advanced coating systems specifically engineered for False Bay beachfront conditions including salt spray, sand abrasion, and moisture. We use premium products that deliver exceptional durability and color retention in demanding coastal environments.',
                  },
                  {
                    title: 'Flexible Scheduling',
                    description:
                      'Understanding of beach lifestyle and tourism patterns with flexible scheduling to minimize disruption. We work around weather conditions, seasonal factors, and your schedule to ensure optimal results and convenience.',
                  },
                  {
                    title: 'Local Community Connection',
                    description:
                      'Strong connections to Muizenberg\'s beach community and respect for the area\'s surfing culture and relaxed lifestyle. We understand what makes this area special and work to enhance properties while maintaining the character that residents and visitors love.',
                  },
                  {
                    title: 'Comprehensive Warranty',
                    description:
                      'Extensive warranty coverage on both materials and workmanship, providing complete peace of mind for your beachfront investment. Our guarantees reflect our confidence in the quality and durability of our work in coastal conditions.',
                  },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20colorful%20Muizenberg%20beach%20property%2C%20vibrant%20beachfront%20house%20painting%20with%20False%20Bay%20views%2C%20quality%20marine-grade%20painting%20equipment%2C%20coastal%20property%20maintenance%20Cape%20Town%20surfing%20village&width=600&height=700&seq=muizenberg-team-work&orientation=portrait"
                alt="Professional beach painting services in Muizenberg"
                className="rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Muizenberg Beach Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our vibrant painting projects in Muizenberg, demonstrating our commitment to
              color excellence and durability in this iconic False Bay beach community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {recentProjects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="w-full h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What Muizenberg Clients Say</h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied beachfront property owners in Muizenberg
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">
                  &quot;{testimonial.text}&quot;
                </p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Add Vibrant Color to Your Muizenberg Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a colorful consultation and detailed quote. Experience specialized
            painting services designed to celebrate Muizenberg&apos;s iconic beach character.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
            <a
              href="tel:+27123456789"
              className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}